import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getJSON } from '@/lib/api'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import type { UnmatchedItem } from './types'
import type { useUnmatchedAction } from './useUnmatchedItems'
import { connectionError } from '../connections/useAddConnection'

type Mutation = ReturnType<typeof useUnmatchedAction>
type Contact = { id: number; name: string; email: string | null }
export function LinkPicker({
  row,
  mutation,
  onClose,
}: {
  row: UnmatchedItem
  mutation: Mutation
  onClose: () => void
}) {
  const [search, setSearch] = useState(row.name_snapshot)
  const [contact, setContact] = useState<Contact | null>(null)
  const candidates = useQuery({
    queryKey: ['unmatched-contacts', search],
    queryFn: () =>
      getJSON<{ contacts: Contact[] }>(`/api/v1/contacts?${new URLSearchParams({ search })}`),
  })
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose()
      }}
    >
      <DialogContent>
        <DialogTitle>Link {row.name_snapshot}</DialogTitle>
        <DialogDescription>
          Choose the contact for {row.waiting_rows} waiting rows.
        </DialogDescription>
        {mutation.error && <Alert>{connectionError(mutation.error)}</Alert>}
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={(value) => {
              setSearch(value)
              setContact(null)
            }}
            placeholder="Search contacts and organisations…"
          />
          <CommandList>
            <CommandEmpty>{candidateStatus(candidates.isPending, candidates.isError)}</CommandEmpty>
            <CommandGroup>
              {candidates.data?.contacts.map((candidate) => (
                <CommandItem
                  key={candidate.id}
                  value={String(candidate.id)}
                  onSelect={() => setContact(candidate)}
                  className="flex-col items-start"
                >
                  <span>{candidate.name}</span>
                  <span className="text-caption text-muted-foreground">{candidate.email}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {contact && <p className="text-body-sm">Selected: {contact.name}</p>}
        <DialogFooter>
          <Button variant="outline" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!contact || mutation.isPending}
            onClick={() => {
              if (contact)
                mutation.mutate(
                  { row, action: 'link', contactId: contact.id },
                  { onSuccess: onClose },
                )
            }}
          >
            Link and write {row.waiting_rows} rows
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
function candidateStatus(pending: boolean, error: boolean) {
  if (pending) return 'Loading contacts…'
  return error ? 'Could not load contacts.' : 'No matching contacts.'
}
export function IgnoreDialog({
  ids,
  row,
  mutation,
  onClose,
  onSuccess,
}: {
  ids: number[]
  row?: UnmatchedItem
  mutation: Mutation
  onClose: () => void
  onSuccess: () => void
}) {
  const label = row ? 'Ignore' : `Ignore ${ids.length}`
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !mutation.isPending) onClose()
      }}
    >
      <DialogContent>
        <DialogTitle>
          {row ? `Ignore ${row.name_snapshot}?` : `Ignore ${ids.length} items?`}
        </DialogTitle>
        <DialogDescription>
          These items will leave the open list. You can un-ignore them from the Ignored filter.
        </DialogDescription>
        {mutation.error && <Alert>{connectionError(mutation.error)}</Alert>}
        <DialogFooter>
          <Button variant="outline" disabled={mutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={mutation.isPending}
            onClick={() =>
              mutation.mutate(row ? { row, action: 'ignore' } : { ids }, { onSuccess })
            }
          >
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
